import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, defineMessages } from 'react-intl'

// Utils
import { useFormatMessage } from 'src/providers/intl'

// Constants
import { SYSTEM_REQUEST, ERRORS } from 'src/constants/messages'
import { MORTGAGE_COACH_TOPIC_KEY } from 'src/constants/topicKeys'

// Actions
import { flashNotification } from 'src/actions/notification'

// Selectors
import {
  selectTcaLeadTimeHours,
  selectTcaSuccess,
  selectTcaError,
  selectTcaUpdated,
  selectTcaReportForRefiDetails,
} from 'src/store/selectors/tca'
import {
  selectContactFirstName,
  selectContactPhotoUri,
} from 'src/store/selectors/contact'

// Components
import { Button, Text, If, Unless, ExternalLinkButton } from 'src/Components'
import { IconExternalLink } from 'src/Components/shared/Icon'
import Question from 'src/Components/shared/Questions/QuestionSingleContainer'
import LineScaleLoader from 'src/Components/shared/Loading/LineScale/LineScale'
import TimeFrame from './TimeFrame'

// Assets
import successGifSource from './mortgage-coach-success.gif'

// Styles
import styles from './MortgageCoachPanel.module.scss'

const propTypes = {
  refiDetails: PropTypes.shape({
    years: PropTypes.number,
    type: PropTypes.string,
  }),
  handleClose: PropTypes.func.isRequired,
}

const messages = defineMessages({
  gifAlt: {
    id: 'MortgageCoach.confirmTCA.successAlt',
    defaultMessage: 'Total Cost Analysis Success',
  },
  placeholder: {
    id: 'MortgageCoach.confirmTCA.question.placeholder',
    defaultMessage: 'Send {firstName} a quick note…',
  },
})

const MortgageCoachStatus = ({ refiDetails, handleClose }) => {
  const dispatch = useDispatch()
  const intlFormat = useFormatMessage()

  const firstName = useSelector(selectContactFirstName)
  const avatarUri = useSelector(selectContactPhotoUri)

  const hours = useSelector(selectTcaLeadTimeHours)
  const success = useSelector(selectTcaSuccess)
  const failure = useSelector(selectTcaError)
  const updated = useSelector(selectTcaUpdated)
  const tcaLinkSrc = useSelector(selectTcaReportForRefiDetails(refiDetails))

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (!mounted) {
      setMounted(true)
    }
  }, [mounted])

  useEffect(() => {
    if (failure) {
      dispatch(
        flashNotification({
          message: ERRORS.communicationError,
          messageType: 'error',
        })
      )
      handleClose()
    }
  }, [failure, handleClose, dispatch])

  return (
    <div className={styles.body}>
      <div className={classnames(styles.info, mounted && styles.mounted)}>
        <Unless condition={success || failure}>
          <div className={styles.fill} />
          <div className={styles.loaderIcon}>
            <LineScaleLoader />
          </div>
          <div className={styles.loaderText}>
            <Text
              className={styles.loaderTextInner}
              element='div'
              size='xl'
              align='center'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='MortgageCoach.tcaResponse.loading1'
                defaultMessage='Building your'
              />
            </Text>
            <Text
              className={styles.loaderTextInner}
              element='div'
              size='xl'
              align='center'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='MortgageCoach.tcaResponse.loading2'
                defaultMessage='Total Cost Analysis...'
              />
            </Text>
          </div>
          <div className={styles.fill} />
        </Unless>
        <If condition={success}>
          <div className={styles.imageWrapper}>
            <img
              className={styles.image}
              src={successGifSource}
              alt={intlFormat(messages.gifAlt)}
            />
          </div>
          <Text
            className={styles.tcaHeader}
            size='xl'
            align='center'
            data-qa='TCAConfirmationMessage'
            variant='neutralCool'
            variantCount='900'
          >
            <Unless condition={updated}>
              <FormattedMessage
                id='MortgageCoach.confirmTCA.headline'
                defaultMessage='Hang tight! Your Total Cost Analysis will be sent via email {timeFrame}.'
                values={{ timeFrame: <TimeFrame hours={hours} /> }}
              />
            </Unless>
            <If condition={updated}>
              <FormattedMessage
                id='MortgageCoach.updateTCA.headline'
                defaultMessage='Sweet! This refi scenario was just added to your Total Cost Analysis'
              />
            </If>
          </Text>
          <If condition={updated}>
            <Text
              className={styles.tcaSubHeader}
              variant='neutralCool'
              variantCount='700'
              align='center'
            >
              <If condition={!tcaLinkSrc && hours >= 0}>
                <FormattedMessage
                  id='MortgageCoach.updateTCA.headline.sub.email'
                  defaultMessage='Check your email for the link {timeFrame}'
                  values={{
                    timeFrame: <TimeFrame hours={hours} />,
                  }}
                />
              </If>
              <If condition={!tcaLinkSrc && hours === -1}>
                <FormattedMessage
                  id='MortgageCoach.updateTCA.headline.sub.manual'
                  defaultMessage='Check your email for the link from {firstName}'
                  values={{ firstName }}
                />
              </If>
            </Text>
          </If>
          <div className={styles.fill} />
          <Unless condition={tcaLinkSrc}>
            <Question
              className={classnames(styles.ctaWidth, styles.ctaQuestion)}
              questionTopic={intlFormat(SYSTEM_REQUEST.mcSuccessTopic)}
              questionTopicKey={MORTGAGE_COACH_TOPIC_KEY}
              placeholder={intlFormat(messages.placeholder, {
                firstName,
              })}
              avatarUri={avatarUri}
            />
          </Unless>
          <If condition={tcaLinkSrc}>
            <ExternalLinkButton
              className={classnames(styles.ctaWidth, styles.ctaButton)}
              primary
              fullWidth
              iconStart={IconExternalLink}
              href={tcaLinkSrc}
            >
              <FormattedMessage
                id='MortgageCoachRequest.tcaStatus.view'
                defaultMessage='View it now'
              />
            </ExternalLinkButton>
          </If>
          <div className={styles.fill} />
        </If>
      </div>
      <Button
        className={classnames(
          styles.ctaWidth,
          styles.modalButton,
          styles.button
        )}
        onClick={handleClose}
        data-qa='completeTCARequest'
      >
        <FormattedMessage
          id='MortgageCoach.successTCA.button'
          defaultMessage='Back to refi options'
        />
      </Button>
    </div>
  )
}

MortgageCoachStatus.propTypes = propTypes

export default MortgageCoachStatus
